import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import { QUOTES_PATH, RamoMark } from '../common/quote-common'
import { useQuoteDrawerModel } from './quote-drawer-model'
import type { QuoteDetailValue, QuoteDrawerProps } from './quote-drawer-model'

export type { QuoteDrawerProps, QuoteDrawerTab } from './quote-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
}

const primaryBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-primary))',
    color: '#fff',
    border: 0,
    fontWeight: 700,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-primary))',
        filter: 'brightness(1.05)',
        boxShadow: 'none',
    },
}

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">{title}</h3>
        {children}
    </section>
)

const ValueList = ({ title, items, empty }: { title: string; items: QuoteDetailValue[]; empty: string }) => (
    <div className="flex flex-col gap-1.5">
        <span className="text-[13px] font-bold text-k-ink">{title} <span className="font-semibold text-k-muted">({items.length})</span></span>
        {items.length === 0 && <p className="m-0 text-sm italic text-k-muted">{empty}</p>}
        {items.length > 0 && (
            <ul className="m-0 flex list-none flex-col p-0">
                {items.map((item) => (
                    <li key={item.key} className="flex items-baseline justify-between gap-3 border-t border-k-line py-2 text-sm first:border-t-0">
                        <span className="min-w-0 text-k-ink-2">{item.descripcion}</span>
                        <span className="shrink-0 text-right font-semibold text-k-ink">{item.valor}</span>
                    </li>
                ))}
            </ul>
        )}
    </div>
)

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line bg-k-surface px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const QuoteDrawer = (props: QuoteDrawerProps) => {
    const { cotizacionId, onClose, onEdit } = props
    const model = useQuoteDrawerModel(props)
    const { cotizacion, loading, status, vencimiento, tracking, fields } = model

    return (
        <>
            <Drawer open={Boolean(cotizacionId)} onClose={onClose} bare width={720}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={cotizacion ? `Detalle de la cotización ${cotizacion.noCotizacion ?? ''}` : 'Detalle de la cotización'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5">
                        <div className="flex items-start gap-3.5">
                            <RamoMark ramo={cotizacion?.ramo} boxed large />
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">
                                    {cotizacion ? `Cotización ${cotizacion.noCotizacion ?? ''}` : 'Detalle de la cotización'}
                                </h2>
                                {cotizacion && !loading && status && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-k-muted">
                                            <span>
                                                {model.tipo}:{' '}
                                                {model.clienteId ? (
                                                    <Link to={`/clientes/${model.clienteId}`} className="font-bold text-k-accent-ink no-underline hover:underline">{model.name || '—'}</Link>
                                                ) : (model.name || '—')}
                                            </span>
                                            {cotizacion.correo && <span>{cotizacion.correo}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${status.className}`}>{status.label}</span>
                                            {cotizacion.ramo && <span className="rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold text-k-accent-ink">{cotizacion.ramo}</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {cotizacion && !loading && (
                            <div className="flex flex-wrap gap-2">
                                <GrantContent permisos={[Permisos.COTIZACIONES_W]}>
                                    <button type="button" className={actionClass} onClick={() => onEdit?.(cotizacion.id)}>
                                        <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                    </button>
                                </GrantContent>
                                <GrantContent permisos={[Permisos.COTIZACIONES_D]}>
                                    <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} onClick={model.askDelete} disabled={model.deleting}>
                                        <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                    </button>
                                </GrantContent>
                            </div>
                        )}

                        <div role="tablist" aria-label="Secciones de la cotización" className="-mx-2 flex gap-0.5 overflow-x-auto">
                            {model.tabs.map((item) => {
                                const selected = model.tab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                        onClick={() => model.setTab(item.id)}
                                    >
                                        {item.label}
                                        {item.count != null && (
                                            <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div role="tabpanel" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {cotizacion && !loading && model.empresaMissing && (
                            <div className="flex items-start gap-2 rounded-lg bg-tone-orange px-4 py-3 text-tone-orange-ink">
                                <WarningRoundedIcon style={{ fontSize: 18 }} />
                                <p className="m-0 text-sm">
                                    Antes de enviar cotizaciones, completa los datos de tu empresa para que se vean más formales.{' '}
                                    <Link to="/configuracion/empresa" state={{ backUrl: QUOTES_PATH }} className="font-bold text-tone-orange-ink underline">Actualizar datos de mi empresa</Link>
                                </p>
                            </div>
                        )}

                        {cotizacion && !loading && model.tab === 'general' && (
                            <>
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Vencimiento</span>
                                        <span className="text-lg font-extrabold tabular-nums">{vencimiento.label}</span>
                                        <span className={`text-xs font-semibold ${vencimiento.noteClass}`}>{vencimiento.note || ' '}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Seguimiento</span>
                                        <span className={`text-lg font-extrabold ${tracking.className}`}>{tracking.label}</span>
                                        <span className="text-xs font-semibold text-k-muted">{tracking.detail || 'El cliente aún no abre el enlace'}</span>
                                    </div>
                                </div>

                                <Section title="Datos generales">
                                    <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                        <Field label="Número">{cotizacion.noCotizacion != null ? String(cotizacion.noCotizacion) : null}</Field>
                                        <Field label="Ramo">{cotizacion.ramo}</Field>
                                        <Field label={model.tipo}>{model.name}</Field>
                                        <Field label="Correo">{cotizacion.correo}</Field>
                                    </dl>
                                </Section>

                                {fields.asegurados && (
                                    <Section title={`Asegurados (${model.asegurados.length})`}>
                                        {model.asegurados.length === 0 && <p className="m-0 text-sm italic text-k-muted">Sin asegurados registrados.</p>}
                                        {model.asegurados.map((item) => (
                                            <div key={item.key} className="flex flex-col gap-0.5 border-t border-k-line pt-2 first:border-t-0 first:pt-0">
                                                <span className="text-sm font-bold text-k-ink">{item.nombre}{item.parentesco ? <span className="font-semibold text-k-muted"> · {item.parentesco}</span> : null}</span>
                                                <span className="text-xs text-k-muted">{item.detail}</span>
                                            </div>
                                        ))}
                                    </Section>
                                )}

                                {fields.vehiculo && (
                                    <Section title="Vehículo">
                                        {model.vehiculos.length === 0 && <p className="m-0 text-sm italic text-k-muted">Sin vehículo registrado.</p>}
                                        {model.vehiculos.map((item) => (
                                            <dl key={item.key} className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                                <Field label="Descripción">{item.descripcion}</Field>
                                                <Field label="Modelo">{item.modelo != null ? String(item.modelo) : null}</Field>
                                            </dl>
                                        ))}
                                    </Section>
                                )}

                                {fields.bien && (
                                    <Section title="Bien asegurado">
                                        <p className={`m-0 whitespace-pre-line break-words text-sm ${cotizacion.texto ? 'text-k-ink-2' : 'italic text-k-muted'}`}>{cotizacion.texto || 'Sin descripción'}</p>
                                    </Section>
                                )}
                            </>
                        )}

                        {cotizacion && !loading && model.tab === 'propuestas' && (
                            <>
                                {model.proposals.length === 0 && <EmptyState title="Sin propuestas" hint="Esta cotización no tiene propuestas capturadas." />}
                                {model.proposals.map((proposal) => (
                                    <section key={proposal.id} className="flex flex-col gap-4 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                                            <h3 className="m-0 text-[15px] font-extrabold text-k-ink">{proposal.title}</h3>
                                            <span className="text-xs font-bold text-k-muted">{proposal.moneda}</span>
                                        </div>
                                        <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                            <Field label="Aseguradora">{proposal.aseguradora}</Field>
                                            <Field label="Plan o paquete">{proposal.plan}</Field>
                                            {proposal.campos.map((campo) => <Field key={campo.label} label={campo.label}>{campo.value}</Field>)}
                                        </dl>
                                        {fields.caracteristicas && <ValueList title="Características adicionales" items={proposal.caracteristicas} empty="Sin características adicionales." />}
                                        <ValueList title="Coberturas" items={proposal.coberturas} empty="Sin coberturas." />
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[13px] font-bold text-k-ink">Planes de pago <span className="font-semibold text-k-muted">({proposal.planes.length})</span></span>
                                            {proposal.planes.length === 0 && <p className="m-0 text-sm italic text-k-muted">Sin planes de pago.</p>}
                                            {proposal.planes.map((plan) => (
                                                <div key={plan.key} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded-xl border border-k-line bg-k-bg px-3.5 py-2.5">
                                                    <span className="flex min-w-0 flex-col">
                                                        <span className="text-sm font-bold text-k-ink">{plan.label}</span>
                                                        <span className="text-xs tabular-nums text-k-muted">{plan.detail}</span>
                                                    </span>
                                                    <span className="text-[15px] font-extrabold tabular-nums text-k-ink">{plan.total}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <span className={`text-xs font-semibold ${tracking.className}`}>
                            {model.trackingText}
                        </span>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                            {cotizacion && !loading && model.viewerUrl && (
                                <a
                                    href={model.viewerUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Abre la cotización como la ve el cliente, sin marcarla como vista"
                                    className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-k-accent-soft px-4 text-sm font-extrabold text-k-accent-ink no-underline"
                                >
                                    <OpenInNewIcon style={{ fontSize: 15 }} />Ver cotización
                                </a>
                            )}
                            {cotizacion && !loading && (
                                <button
                                    type="button"
                                    className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-k-primary px-4 text-sm font-bold text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                                    disabled={!cotizacion.correo || model.sending}
                                    title={cotizacion.correo ? `Enviar a ${cotizacion.correo}` : 'La cotización no tiene correo; edítala para capturarlo'}
                                    onClick={model.askSend}
                                >
                                    <SendOutlinedIcon style={{ fontSize: 15 }} />{model.sending ? 'Enviando…' : 'Enviar'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.confirmSend} onClose={model.cancelSend} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Enviar cotización</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">Se enviará la cotización por correo a <strong>{cotizacion?.correo}</strong>. ¿Desea continuar?</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelSend}>Cancelar</Button>
                    <Button variant="contained" sx={primaryBtnSx} onClick={model.sendCotizacion} disabled={model.sending}>Enviar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar cotización</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la cotización? Si continúa, se perderá toda su información.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteCotizacion} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {model.toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {model.toast}
                </div>
            )}
        </>
    )
}

export default QuoteDrawer
